<template>
  <BaseDialog
    centered
    v-if="opportunityStatus"
    contentClass="delete-opportunity-status"
    ref="dialog"
    :show-actions="true"
  >
    <template slot="title">{{ $t('page.admin-opportunity-management.delete-dialog.title') }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <div class="delete-opportunity-status__current-status">
          <span class="delete-opportunity-status__current-status-text">{{
            $t('page.admin-opportunity-management.delete-dialog.are-you-sure')
          }}</span>
          <BaseStatusBadge :color="opportunityStatus.color" :text="opportunityStatus.name" />
        </div>
        <div v-if="affectedOpportunitiesCount > 0" class="delete-opportunity-status__warning-notice">
          <div class="delete-opportunity-status__alert-icon">
            <BaseIcon name="alert-circle" />
          </div>
          <div class="delete-opportunity-status__warning-notice-text">
            <BaseCircularLoadingBar v-if="affectedOpportunitiesCountPending" :size="14" :width="2" color="accent" />
            <template v-else>
              <span>{{
                $tc('page.admin-opportunity-management.delete-dialog.warning', affectedOpportunitiesCount, {
                  opportunitiesNumber: affectedOpportunitiesCount,
                })
              }}</span>
              <span>{{
                $tc('page.admin-opportunity-management.delete-dialog.transfer-status', affectedOpportunitiesCount)
              }}</span>
            </template>
          </div>
        </div>
        <div v-if="affectedOpportunitiesCount > 0">
          <FormLabel is-required>{{ $t('page.admin-opportunity-management.delete-dialog.change-status') }}</FormLabel>
          <BaseSelect
            name="statusSelect"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            :value="newOpportunityStatus"
            @change="(newStatus) => (newOpportunityStatus = newStatus)"
          />
        </div>
        <div class="delete-opportunity-status__actions">
          <FormButton is-outlined class="delete-opportunity-status__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="!hasChanged">
            <span>{{ $t('actions.confirm') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import { mapActions, mapGetters } from 'vuex';
import FormLabel from '@/components/form/FormLabel.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BaseStatusBadge from '@/components/base/BaseStatusBadge.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';

export default {
  name: 'DeleteOpportunityStatusDialog',
  components: {
    BaseCircularLoadingBar,
    BaseIcon,
    BaseStatusBadge,
    BaseSelect,
    FormLabel,
    BaseDialog,
    FormButton,
  },
  data() {
    return {
      opportunityStatus: null,
      newOpportunityStatus: null,
      affectedOpportunitiesCount: 0,
    };
  },
  computed: {
    ...mapGetters('opportunityStatuses', ['opportunityStatuses']),
    ...mapGetters('opportunities', {
      affectedOpportunitiesCountPending: 'pending',
    }),
    hasChanged() {
      return (
        this.affectedOpportunitiesCount <= 0 ||
        (this.opportunityStatus &&
          this.newOpportunityStatus &&
          this.opportunityStatus.id !== this.newOpportunityStatus.id)
      );
    },
    statusOptions() {
      return this.opportunityStatuses.map((status) => ({ label: status.name, value: status }));
    },
  },
  methods: {
    ...mapActions('opportunityStatuses', ['deleteOpportunityStatus']),
    ...mapActions('opportunities', [
      'getOpportunities',
      'searchOpportunities',
      'setOpportunityStatusSearchFilter',
      'updateOpportunitiesStatus',
      'setOpportunities',
    ]),
    openDialog(opportunityStatus) {
      this.opportunityStatus = opportunityStatus;
      this.setOpportunityStatusSearchFilter([this.opportunityStatus.id]);
      this.getOpportunities(1).then(({ pagination }) => {
        this.affectedOpportunitiesCount = pagination.totalCount;
      });
      this.$nextTick(() => {
        this.$refs.dialog.openModal();
      });
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    onSubmit() {
      if (this.affectedOpportunitiesCount > 0) {
        const payload = {
          newOpportunityStatusId: this.newOpportunityStatus.id,
          filters: { statusId: this.opportunityStatus.id },
        };
        this.updateOpportunitiesStatus(payload)
          .then(() => {
            //refresh the existing opportunities in store
            this.setOpportunityStatusSearchFilter([]);
            this.searchOpportunities(1);
            this.deleteOpportunityStatus(this.opportunityStatus.id).then(() => {
              this.$toast.success(
                this.$t('page.admin-opportunity-management.delete-dialog.delete-opportunity-status-success'),
              );
              this.$emit('action-succeeded');
            });
          })
          .catch((error) => this.$toast.error(error));
      } else {
        this.deleteOpportunityStatus(this.opportunityStatus.id)
          .then(() => {
            this.$toast.success(
              this.$t('page.admin-opportunity-management.delete-dialog.delete-opportunity-status-success'),
            );
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      }
      this.closeDialog();
    },
    handleKeyup(event) {
      const keyCode = event.keyCode || event.charCode;

      if (keyCode === 13 && this.hasChanged) {
        this.onSubmit();
      }
    },
    cancelClick() {
      this.closeDialog();
    },
  },
  created() {
    document.addEventListener('keyup', this.handleKeyup);
  },
  destroyed() {
    document.removeEventListener('keyup', this.handleKeyup);
  },
};
</script>

<style lang="scss">
.base-dialog__delete-opportunity-status {
  height: auto;
  width: 800px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.delete-opportunity-status {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__action {
    text-transform: lowercase;
    font-weight: bold;
  }

  &__current-status {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-text {
      color: $color-normal;
    }
  }

  &__warning-notice {
    margin: 32px 0 16px 0;
    font-size: 14px;
    background: $color-red-100;
    padding: 8px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    gap: 16px;

    &-text {
      display: flex;
      flex-direction: column;
      gap: 8px;
      font-size: 16px;

      span:first-child {
        font-weight: 700;
      }
    }
  }

  &__alert-icon {
    width: 24px;
    height: 24px;
    color: $color-error;
  }
}
</style>
